import { CustomerDTO, SiteDTO } from "@beable/roster-backend-client-v1";
import React, { ChangeEvent, useEffect, useState } from "react";
import CustomerSelector from "../../Common/CustomerSelector";
import { DatePicker } from "../../Common/DatePicker/DatePicker";
import SiteSelector from "../../Common/SiteSelector";
import FormControlLabel from '@mui/material/FormControlLabel';
import RadioGroup from '@mui/material/RadioGroup';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
import Radio from '@mui/material/Radio';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import { styled } from '@mui/material/styles';
import { EditableEngagement, EngagementScheduleWithSiteVisibility, ToastNotification } from "./types";
import { FormikProps, FormikErrors } from "formik";
import { createUUID } from '@beable/binary-uuid-ts';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import DeleteIcon from '@mui/icons-material/Delete';
import ScheduleIcon from '@mui/icons-material/Schedule';

const ScheduleContainer = styled(Grid)(() => ({
    border: '1px solid #cccccc',
    borderRadius: '5px',
    marginBottom: '10px'
}));

const ScheduleDateContainer = styled(Grid)(() => ({
    borderRadius: '5px',
    background: '#f5f0fc',
    padding: '5px 10px'
}));

const LabelText = styled(Typography)(() => ({
    fontWeight: 'bold',
    fontSize: '14px',
    marginRight: '5px',
    display: 'inline'
}));

const ScheduleRuleChip = styled(Chip)(() => ({
    fontFamily: 'monospace'
}));

interface EditScheduleProps {
    formik: FormikProps<EditableEngagement>;
    schedule: EngagementScheduleWithSiteVisibility;
    setNotification: React.Dispatch<React.SetStateAction<ToastNotification | null>>;
    fieldValuePrefix?: string;
    errors?: FormikErrors<EngagementScheduleWithSiteVisibility> | undefined;
}

export default function EditSchedule({ formik, schedule, fieldValuePrefix, errors, setNotification }: EditScheduleProps) {
    const [site, setSite] = useState<SiteDTO | null>(null);
    const [customer, setCustomer] = useState<CustomerDTO | null>(null);
    /**
     * Utility function for manipulating arrays since we can't use FieldArray and arrayHelpers
     */
    const setField = (field: string, value: any, shouldValidate?: boolean) => {
        formik.setFieldValue(`${fieldValuePrefix}.${field}`, value, shouldValidate);
        formik.setFieldTouched(`${fieldValuePrefix}.${field}`);
    };

    /**
     * Each time a Site is selected add a rule for it based on the current 
     * visibility selection: 'AllSites' | 'AllSitesExcept' | 'SpecificSites'
     */
    useEffect(() => {
        if (!site) {
            return;
        }

        const siteAlreadyIncluded = schedule.rules.some(r => r.siteId === site.id);
        if (siteAlreadyIncluded) {
            setNotification({ 
                error: true, 
                message: `Site ID: ${ site.id} already added` 
            });
            return;
        }

        const newRule = {
            id: createUUID(),
            siteId: site.id,
            include: schedule.visibility === 'SpecificSites',
            exclude: schedule.visibility === 'AllSitesExcept'
        };
        setField(`rules`, [...schedule.rules, newRule]);
        setCustomer(null);
    }, [site]);

    /**
     * When the Visibility Radio selection changes any existing `rules` are modified to match
     * the new visibility
     */
    const handleVisibilityChange = (event: ChangeEvent<HTMLInputElement>, newVisibility: string) => {
        formik.handleChange(event);
        const updatedRules = newVisibility === 'AllSites' ? [] : schedule.rules.map(rule => ({
            ...rule,
            include: newVisibility === 'SpecificSites',
            exclude: newVisibility === 'AllSitesExcept'
        }));
        setField(`rules`, updatedRules);
    };

    const onRemoveSchedule = () => {
        const newSchedule = formik.values.schedules.filter(s => s.id !== schedule.id);
        formik.setFieldValue('schedules', newSchedule);
        formik.setFieldTouched('schedules');
    };

    const showCustomerSelector = ['AllSitesExcept', 'SpecificSites'].includes(schedule.visibility);

    return (
        <ScheduleContainer container key={schedule.id}>
            <Grid size={12}>
                <ScheduleDateContainer container>
                    <Grid
                        size={6}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between'
                        }}>
                        <label>
                            <Tooltip 
                                title={
                                <Box
                                    sx={{
                                        display: 'flex',
                                        gap: 1
                                    }}>
                                    <ScheduleIcon/>
                                    <span>Will appear starting at midnight on the selected day.</span>
                                </Box>
                                }
                            >
                                <LabelText>Start Showing:</LabelText>
                            </Tooltip>

                            <DatePicker
                                value={new Date(schedule.startAt)}
                                onChange={(date) => { 
                                    if (date) {
                                        date.setHours(0,0,0,0); 
                                    }
                                    setField(`startAt`, date);
                                }}
                                name={`${fieldValuePrefix}.startAt`}
                                error={errors?.startAt as string}
                            />
                        </label>
                        <label>
                            <Tooltip 
                                title={
                                <Box
                                    sx={{
                                        display: 'flex',
                                        gap: 1
                                    }}>
                                    <ScheduleIcon/>
                                    <span>Will stop starting at the end of the selected day.</span>
                                </Box>
                                }
                            >
                                <LabelText>Stop Showing:</LabelText>
                            </Tooltip>
                            <DatePicker
                                value={schedule.endAt ? new Date(schedule.endAt) : null}
                                onChange={(date) => {
                                    if (date) {
                                        date.setHours(23,59,59,59);
                                    }
                                    setField(`endAt`, date);
                                }}
                                name={`${fieldValuePrefix}.endAt`}
                                minDate={schedule.startAt}
                                error={errors?.endAt}

                            />
                        </label>
                    </Grid>
                    <Grid
                        size={5}
                        sx={{
                            display: 'flex',
                            justifyContent: 'end'
                        }}>
                        <RadioGroup
                            aria-label="configure how to show on sites"
                            name={`${fieldValuePrefix}.visibility`}
                            value={schedule.visibility}
                            onChange={handleVisibilityChange}
                            row={true}
                        >
                            <FormControlLabel
                                value='AllSites'
                                control={<Radio />}
                                label='All Sites'
                                onChange={formik.handleChange}
                                slotProps={{
                                    typography: {
                                        sx: { fontSize: '14px' }
                                    }
                                }}
                            />
                            <FormControlLabel
                                value='AllSitesExcept'
                                control={<Radio />}
                                label='All Sites Except'
                                onChange={formik.handleChange}
                                slotProps={{
                                    typography: {
                                        sx: { fontSize: '14px' }
                                    }
                                }}
                            />
                            <FormControlLabel
                                value='SpecificSites'
                                control={<Radio />}
                                label='Specific Sites'
                                onChange={formik.handleChange}
                                slotProps={{
                                    typography: {
                                        sx: { fontSize: '14px' }
                                    }
                                }}
                            />
                        </RadioGroup>

                    </Grid>
                    <Grid
                        size={1}
                        sx={{
                            display: 'flex',
                            justifyContent: 'end'
                        }}>
                        <IconButton
                            onClick={onRemoveSchedule}
                            aria-label="remove schedule"
                            color="inherit"
                            size="large">
                            <DeleteIcon />
                        </IconButton>
                    </Grid>
                </ScheduleDateContainer>
            </Grid>
            {showCustomerSelector && <Grid sx={{ padding: '5px 10px' }} size={12}>
                <Box sx={{
                    display: 'flex'
                }}>

                    <CustomerSelector
                        onSelect={(selectedCustomer: CustomerDTO) =>  { 
                            setCustomer(selectedCustomer);
                          }}
                        variant="outlined"
                        hideSearch
                        ignoreState
                    />

                    <SiteSelector
                        customerId={customer ? customer.id : ""}
                        onSelect={(siteDTO: SiteDTO) => {
                            if (!siteDTO) {
                                return;
                            }
                            setSite(siteDTO);
                        }}
                        variant={"outlined"}
                        disabled={!customer}
                        ignoreState
                    />
                </Box>
                <Box>
                    <Divider />
                    <Stack
                        direction="row"
                        spacing={2}
                        useFlexGap
                        sx={{
                            paddingTop: 2,
                            paddingBottom: 2,
                            flexWrap: "wrap"
                        }}>
                        {schedule.rules.map((rule) => (
                            <ScheduleRuleChip
                                key={rule.id}
                                label={`${rule.include ? 'including' : 'excluding'} Site ID: ${rule.siteId}`}
                                onDelete={() => {
                                    setField(`rules`, schedule.rules.filter(r => r.siteId !== rule.siteId));
                                }}
                                deleteIcon={<DeleteIcon />}
                                variant="outlined"
                            />
                        ))}
                    </Stack>

                </Box>
            </Grid>
            }
        </ScheduleContainer >
    );
}